
import React, { useState } from 'react';
import { Copy, Link, AlertTriangle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Bot } from '@/types/trader';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface ConnectionSettingsCardProps {
  bot: Bot | null | undefined;
  isLoading: boolean;
}

export const ConnectionSettingsCard = ({ bot, isLoading }: ConnectionSettingsCardProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [showToken, setShowToken] = useState(false);

  const copyToClipboard = (text: string, description: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        toast({
          title: t("copied_to_clipboard"),
          description
        });
      },
      (err) => {
        console.error('Could not copy text: ', err);
        toast({
          title: t("failed_to_copy"),
          description: t("failed_to_copy"),
          variant: "destructive"
        });
      }
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle><Skeleton className="h-6 w-36" /></CardTitle>
          <CardDescription><Skeleton className="h-4 w-48" /></CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </CardContent>
      </Card>
    );
  }

  if (!bot) return null;

  const tradingViewAlertMessage = `{
  "token": "${bot.signal_token}",
  "action": "{{strategy.order.action}}",
  "symbol": "{{ticker}}",
  "amount": {{strategy.order.contracts}},
  "alert_message": {{strategy.order.alert_message}}
}`;

  const toggleTokenVisibility = () => {
    setShowToken(!showToken);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link size={18} />
          {t('connection_settings')}
        </CardTitle>
        <CardDescription>
          {t("connection_settings_desc")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="text-sm text-muted-foreground mb-1">{t("webhook_url")}:</div>
          <div className="flex items-center gap-2">
            <div className="bg-muted p-2 rounded-md text-xs font-mono truncate flex-1">
              {bot.webhook_url}
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              aria-label={t("copy")}
              onClick={() => copyToClipboard(bot.webhook_url, t("webhook_url") + " " + t("copied_to_clipboard"))}
            >
              <Copy size={14} />
            </Button>
          </div>
        </div>

        <div>
          <div className="text-sm text-muted-foreground mb-1">{t("signal_token")}:</div>
          <div className="flex items-center gap-2">
            <div className="bg-muted p-2 rounded-md text-xs font-mono truncate flex-1">
              {showToken ? bot.signal_token : '••••••••••••••••••••••••••••••••'}
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              aria-label={showToken ? t('hide') : t('show')}
              onClick={toggleTokenVisibility}
            >
              {showToken ? t('hide') : t('show')}
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              aria-label={t('copy')}
              onClick={() => copyToClipboard(bot.signal_token, t("signal_token") + " " + t("copied_to_clipboard"))}
            >
              <Copy size={14} />
            </Button>
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <div className="w-full">
          <div className="text-sm font-medium mb-2">{t("tradingview_alert_message")}:</div>
          <div className="relative">
            <pre className="bg-muted p-2 rounded-md text-xs font-mono overflow-auto max-h-36">
              {tradingViewAlertMessage}
            </pre>
            <Button 
              className="absolute top-2 right-2" 
              size="sm" 
              variant="secondary"
              aria-label={t('copy')}
              onClick={() => copyToClipboard(tradingViewAlertMessage, t("tradingview_alert_message") + " " + t("copied_to_clipboard"))}
            >
              <Copy size={14} className="mr-1" />
              {t('copy')}
            </Button>
          </div>
          <div className="flex items-center gap-2 mt-2 text-xs text-amber-500">
            <AlertTriangle size={14} />
            <span>{t("tv_alert_help")}</span>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};
